import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarClock, Sparkles, Bell, MapPin, Music2, ChevronRight, AlertCircle, Users, Search, Mail, Megaphone } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import {
  OrientationCard,
  ProfileBanner,
  AvailabilityBanner,
  isOrientationDismissed,
  dismissOrientation,
} from "../components/Onboarding";
import AnnouncementBanner from "../components/AnnouncementBanner";

function dayLabel(iso) {
  if (!iso) return "";
  const d = new Date(iso + "T00:00:00");
  return d.toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" });
}

function relativeWhen(today, iso) {
  if (!iso) return "";
  if (iso === today) return "Today";
  const d = new Date(iso + "T00:00:00");
  const t = new Date(today + "T00:00:00");
  const diff = Math.round((d - t) / 86400000);
  if (diff === 1) return "Tomorrow";
  return d.toLocaleDateString(undefined, { weekday: "long" });
}

function notifIcon(kind) {
  switch (kind) {
    case "booking_request": return CalendarClock;
    case "booking_response": return CalendarClock;
    case "rehearsal_update": return Music2;
    case "announcement": return Megaphone;
    default: return Bell;
  }
}

export default function Home() {
  const { user } = useAuth();
  // Show last-known data instantly on Safari while we re-fetch — avoids the
  // perceived "long blank load" between Apple's strict cookie negotiation
  // and the API round-trip.
  const [data, setData] = useState(() => {
    try {
      const raw = window.localStorage.getItem("amds_home_cache_v1");
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  });
  const [orientationDismissed, setOrientationDismissed] = useState(isOrientationDismissed());

  useEffect(() => {
    api.get("/home").then(({ data }) => {
      setData(data);
      try { window.localStorage.setItem("amds_home_cache_v1", JSON.stringify(data)); } catch {}
    }).catch(() => { /* keep stale cache visible */ });
  }, []);

  const today = data?.today?.date;

  // Group this-week items by date, excluding today.
  // Phase 2 — diary events are the primary source. Bookings/rehearsals are
  // still included for backward compatibility (auto-created events already
  // dedupe by source_id but legacy bookings without diary events still need
  // to show through).
  const weekByDate = useMemo(() => {
    const map = new Map();
    if (!data) return map;
    const push = (date, type, item) => {
      if (!date) return;
      if (date === today) return;
      const day = date.slice(0, 10);
      if (!map.has(day)) map.set(day, []);
      map.get(day).push({ type, item });
    };
    (data.this_week?.diary_events || []).forEach((e) => push(e.date, "event", e));
    const eventBookingIds = new Set(
      (data.this_week?.diary_events || [])
        .filter((e) => e.source_type === "booking")
        .map((e) => e.source_id),
    );
    (data.this_week?.bookings || []).forEach((b) => {
      if (eventBookingIds.has(b.id)) return; // already represented as event
      push(b.date, "booking", b);
    });
    (data.this_week?.rehearsals || []).forEach((r) => push(r.date, "rehearsal", r));
    return new Map([...map.entries()].sort());
  }, [data, today]);

  if (!data) {
    // Lightweight skeleton — Safari renders this instantly while /home loads,
    // so the user never sees a blank screen.
    return (
      <div className="space-y-6 max-w-3xl pb-12 animate-pulse" data-testid="home-loading">
        <div>
          <div className="h-3 w-24 bg-ivory-200 rounded mb-2" />
          <div className="h-8 w-48 bg-ivory-200 rounded" />
        </div>
        <div className="grid grid-cols-4 gap-2">
          {[0,1,2,3].map((i) => <div key={i} className="card-base p-3 h-16" />)}
        </div>
        <div className="card-base p-5 space-y-2">
          <div className="h-4 w-24 bg-ivory-200 rounded" />
          <div className="h-12 w-full bg-ivory-100 rounded-xl" />
          <div className="h-12 w-full bg-ivory-100 rounded-xl" />
        </div>
      </div>
    );
  }

  const todayItems = [
    ...(data.today.diary_events || []).map((e) => ({ type: "event", item: e })),
    ...(data.today.bookings || [])
      .filter((b) => !(data.today.diary_events || []).some(
        (e) => e.source_type === "booking" && e.source_id === b.id,
      ))
      .map((b) => ({ type: "booking", item: b })),
    ...(data.today.rehearsals || []).map((r) => ({ type: "rehearsal", item: r })),
  ];

  return (
    <div className="space-y-6 max-w-3xl pb-12" data-testid="home-page">
      {/* Header */}
      <div>
        <div className="label-overline">Today · {dayLabel(today)}</div>
        <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mt-1">
          Hi, {user?.name?.split(" ")[0] || "there"}.
        </h1>
        <p className="text-ink-600 text-sm mt-1">What's on, what's coming, and what needs a yes.</p>
      </div>

      <AnnouncementBanner />

      {/* F011 — Orientation card (top) — auto-hides when complete or dismissed */}
      {!orientationDismissed && data.onboarding && (
        <OrientationCard
          onboarding={data.onboarding}
          onDismiss={() => { dismissOrientation(); setOrientationDismissed(true); }}
        />
      )}

      {/* F012 / F013 — Persistent banners, only after the orientation card is dismissed */}
      {orientationDismissed && data.onboarding && (
        <>
          {data.onboarding.role === "musician" && !data.onboarding.has_instrument && <ProfileBanner />}
          {!data.onboarding.has_availability && <AvailabilityBanner />}
        </>
      )}

      {/* Quick links — keep within 1 tap from Home */}
      <div className="grid grid-cols-4 gap-2" data-testid="home-quicklinks">
        <Link to="/ensembles" className="card-base p-3 flex flex-col items-center gap-1 hover:shadow-soft transition" data-testid="home-quick-ensembles">
          <Users size={18} className="text-sage-600" />
          <span className="text-xs font-medium">Ensembles</span>
        </Link>
        <Link to="/contacts" className="card-base p-3 flex flex-col items-center gap-1 hover:shadow-soft transition" data-testid="home-quick-contacts">
          <Mail size={18} className="text-sage-600" />
          <span className="text-xs font-medium">Contacts</span>
        </Link>
        <Link to={user?.role === "musician" ? "/diary" : "/search"} className="card-base p-3 flex flex-col items-center gap-1 hover:shadow-soft transition" data-testid="home-quick-search">
          {user?.role === "musician" ? <CalendarClock size={18} className="text-sage-600" /> : <Search size={18} className="text-sage-600" />}
          <span className="text-xs font-medium">{user?.role === "musician" ? "My diary" : "Find players"}</span>
        </Link>
        <Link to="/gigs" className="card-base p-3 flex flex-col items-center gap-1 hover:shadow-soft transition" data-testid="home-quick-gigs">
          <Music2 size={18} className="text-sage-600" />
          <span className="text-xs font-medium">Dep board</span>
        </Link>
      </div>

      {/* TODAY */}
      <section className="card-base p-5" data-testid="home-today">
        <div className="flex items-center gap-2 mb-3">
          <Sparkles size={16} className="text-terracotta-600" />
          <h2 className="font-heading text-lg font-semibold">Today</h2>
        </div>
        {todayItems.length === 0 ? (
          <p className="text-sm text-ink-600">Nothing in the diary today. Enjoy the breathing room.</p>
        ) : (
          <ul className="space-y-2.5">
            {todayItems.map((entry, i) => (
              <EventRow key={i} {...entry} />
            ))}
          </ul>
        )}
      </section>

      {/* TODAY'S REHEARSAL INFO */}
      {data.today.rehearsal_info?.length > 0 && (
        <section className="card-base p-5" data-testid="home-rehearsal-info">
          <div className="flex items-center gap-2 mb-3">
            <Music2 size={16} className="text-sage-600" />
            <h2 className="font-heading text-lg font-semibold">Today's rehearsal info</h2>
          </div>
          <ul className="space-y-3">
            {data.today.rehearsal_info.map((r) => (
              <li key={r.id} className="rounded-xl border border-ivory-200 p-3" data-testid="rehearsal-info-row">
                <div className="font-medium">{r.title}</div>
                <div className="text-sm text-ink-600">{r.ensemble_name}</div>
                <div className="mt-1.5 flex flex-wrap gap-3 text-sm text-ink-700">
                  {r.start_time && <span className="inline-flex items-center gap-1"><CalendarClock size={14} />{r.start_time}</span>}
                  {r.venue && <span className="inline-flex items-center gap-1"><MapPin size={14} />{r.venue}</span>}
                </div>
                {r.notes && <p className="text-sm text-ink-600 mt-2 whitespace-pre-wrap">{r.notes}</p>}
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* THIS WEEK */}
      <section className="card-base p-5" data-testid="home-this-week">
        <div className="flex items-center gap-2 mb-3">
          <CalendarClock size={16} className="text-sage-600" />
          <h2 className="font-heading text-lg font-semibold">Next 7 days</h2>
        </div>
        {weekByDate.size === 0 ? (
          <p className="text-sm text-ink-600">No accepted bookings or rehearsals in the next 7 days.</p>
        ) : (
          <ul className="space-y-3">
            {[...weekByDate.entries()].map(([date, entries]) => (
              <li key={date} data-testid="week-day-group">
                <div className="text-xs uppercase tracking-widest text-ink-500 mb-1.5">
                  {relativeWhen(today, date)} · {dayLabel(date)}
                </div>
                <ul className="space-y-2">
                  {entries.map((entry, i) => <EventRow key={i} {...entry} />)}
                </ul>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* NOTIFICATIONS — only the ones needing action */}
      <section className="card-base p-5" data-testid="home-notifications">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Bell size={16} className="text-amber-deep" />
            <h2 className="font-heading text-lg font-semibold">Needs your attention</h2>
          </div>
          {data.unread_count > 0 && (
            <span className="pill bg-amber-soft text-amber-deep" data-testid="home-notif-count">{data.unread_count} unread</span>
          )}
        </div>
        {(data.notifications || []).length === 0 ? (
          <p className="text-sm text-ink-600">You're all caught up.</p>
        ) : (
          <ul className="space-y-2">
            {data.notifications.map((n) => {
              const Icon = notifIcon(n.kind);
              const content = (
                <div className="rounded-xl border border-ivory-200 p-3 hover:bg-ivory-50 transition flex items-start gap-3" data-testid="notif-row">
                  <Icon size={18} className="text-sage-600 mt-0.5 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm">{n.title}</div>
                    {n.body && <div className="text-sm text-ink-600 truncate">{n.body}</div>}
                  </div>
                  <ChevronRight size={16} className="text-ink-400 shrink-0" />
                </div>
              );
              return (
                <li key={n.id}>
                  {n.link ? <Link to={n.link}>{content}</Link> : content}
                </li>
              );
            })}
          </ul>
        )}
      </section>

      {/* Soft tip */}
      <div className="text-xs text-ink-500 flex items-start gap-2" data-testid="home-tip">
        <AlertCircle size={14} className="mt-0.5 shrink-0" />
        <span>This is your operational home — only what's happening soon. Use Diary for the wider view.</span>
      </div>
    </div>
  );
}

function EventRow({ type, item }) {
  // Phase 2 — diary events take a slightly different shape than bookings/rehearsals.
  if (type === "event") {
    const start = item.start_time || "";
    const end = item.end_time || "";
    const time = start && end ? `${start}–${end}` : (start || end || "All day");
    const venue = item.venue || "";
    const subtitle = item.organisation || "";
    const impact = item.availability_impact || "unavailable";
    const tone =
      impact === "available" ? "bg-sage-100 text-sage-600" :
      impact === "tentative" ? "bg-amber-100 text-amber-deep" :
      "bg-terracotta-50 text-terracotta-600";
    const body = (
      <div className="rounded-xl border border-ivory-200 p-3 flex items-center gap-3 hover:bg-ivory-50 transition" data-testid="home-event-event">
        <div className={`w-9 h-9 rounded-xl grid place-items-center shrink-0 ${tone}`}>
          <CalendarClock size={16} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-medium text-sm truncate">{item.title || "Event"}</div>
          <div className="text-xs text-ink-500 truncate">
            {[time, venue, subtitle].filter(Boolean).join(" · ")}
          </div>
        </div>
        <ChevronRight size={16} className="text-ink-400 shrink-0" />
      </div>
    );
    const link = item.source_type === "booking" && item.source_id ? `/bookings/${item.source_id}` : "/diary";
    return <li>{<Link to={link}>{body}</Link>}</li>;
  }

  const time = item.start_time || item.call_time || item.start_at?.slice(11, 16) || "";
  const venue = item.venue || item.location || "";
  const title =
    type === "booking"
      ? (item.event_type ? item.event_type.replace("_", " ") : "Booking") + (item.title ? ` · ${item.title}` : "")
      : (item.title || item.name || "Rehearsal");
  const subtitle = type === "rehearsal" ? (item.ensemble_name || "") : (item.musician_name || item.booker_name || "");

  const body = (
    <div className="rounded-xl border border-ivory-200 p-3 flex items-center gap-3 hover:bg-ivory-50 transition" data-testid={`home-event-${type}`}>
      <div className={`w-9 h-9 rounded-xl grid place-items-center shrink-0 ${type === "rehearsal" ? "bg-sage-100 text-sage-600" : "bg-terracotta-50 text-terracotta-600"}`}>
        {type === "rehearsal" ? <Music2 size={16} /> : <CalendarClock size={16} />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-medium text-sm capitalize">{title}</div>
        <div className="text-xs text-ink-500 truncate">
          {[time, venue, subtitle].filter(Boolean).join(" · ") || "Confirmed"}
        </div>
      </div>
      <ChevronRight size={16} className="text-ink-400 shrink-0" />
    </div>
  );
  const link = type === "booking" && item.id ? `/bookings/${item.id}` : (type === "rehearsal" && item.ensemble_id ? `/ensembles/${item.ensemble_id}` : null);
  return <li>{link ? <Link to={link}>{body}</Link> : body}</li>;
}
